import React, { createContext, useRef, useCallback, useState, useEffect, useContext } from 'react';
import io from 'socket.io-client';
import { UserContext } from './userContext';

export const SocketContext = createContext();

export const SocketProvider = ({ children }) => {
    const { accessToken, user } = useContext(UserContext) || {};
    const socketRef = useRef(null);
    const isSocketReadyRef = useRef(false);
    const [socketReady, setSocketReady] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authError, setAuthError] = useState(null); // ✅ NEW: Track auth errors
    const socketConnectErrorLoggedRef = useRef(false);
    
    const subscribersRef = useRef([]);
    const notificationsRef = useRef([]);
    const signedRef = useRef([]);
    const screeningRef = useRef([]);
    const screeningPaymentRef = useRef([]);
    const countRef = useRef([]);
    const chatErrorRef = useRef([]);
    
    // ✅ NEW: Track pending auth to avoid race conditions
    const pendingAuthRef = useRef(null);
    const authTimeoutRef = useRef(null);

    const requestNonUserChatToken = useCallback(async ({ memberId, memberType, groupId }) => {
        if (!memberId || !memberType || !groupId) {
            throw new Error('memberId, memberType, and groupId are required');
        }

        const response = await fetch('/api/chat/auth-token/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                member_id: String(memberId),
                member_type: String(memberType).toUpperCase(),
                group_id: String(groupId),
            }),
        });

        let payload = {};
        try {
            payload = await response.json();
        } catch (error) {
            payload = {};
        }

        if (!response.ok || !payload?.token) {
            throw new Error(payload?.error || 'Failed to fetch chat auth token');
        }

        return payload.token;
    }, []);

    // ✅ NEW: Separate auto-auth for users
    useEffect(() => {
        if (user && isSocketReadyRef.current && accessToken && socketRef.current && !isAuthenticated) {
            if (pendingAuthRef.current === `USER-${user.id}`) {
                return;
            }
            console.log("🔁 Auto-authenticating USER with accessToken...");
            pendingAuthRef.current = `USER-${user.id}`;
            socketRef.current.emit('authenticate', {
                token: accessToken,
                member_type: "USER"
            });
        }
    }, [user, accessToken, socketReady, isAuthenticated]);

    // ✅ CRITICAL: Socket initialization - only once
    const initializeSocket = useCallback(() => {
        if (socketRef.current) {
            console.log("⚠️ Socket already initialized, skipping...");
            return;
        }

        console.log("🚀 Initializing socket...");
        socketRef.current = io('/chat', {
            autoConnect: false,
            reconnection: true,
            reconnectionAttempts: 3,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 5000,
        });

        socketRef.current.on('connect', () => {
            console.log("✅ Socket Connected");
            socketConnectErrorLoggedRef.current = false;
            isSocketReadyRef.current = true;
            setSocketReady(true);
            setIsAuthenticated(false);
            setAuthError(null);
        });

        // ✅ CRITICAL: This handler runs EVERY TIME 'authenticated' is emitted
        socketRef.current.on('authenticated', (data) => {
            console.log("📡 Received 'authenticated' event:", data);
            if (data?.status === 'ok') {
                console.log("✅ Successfully authenticated!");
                setIsAuthenticated(true);
                setAuthError(null);
                // Clear pending auth
                pendingAuthRef.current = null;
                if (authTimeoutRef.current) {
                    clearTimeout(authTimeoutRef.current);
                    authTimeoutRef.current = null;
                }
            } else {
                console.error("❌ Authentication failed:", data?.error || 'Unknown error');
                setIsAuthenticated(false);
                setAuthError(data?.error || 'Authentication failed');
            }
        });

        socketRef.current.on('disconnect', (reason) => {
            console.warn("⚠️ Socket Disconnected. Reason:", reason);
            isSocketReadyRef.current = false;
            setSocketReady(false);
            setIsAuthenticated(false);
            pendingAuthRef.current = null;
            if (authTimeoutRef.current) {
                clearTimeout(authTimeoutRef.current);
            }
        });

        socketRef.current.on('connect_error', (error) => {
            if (!socketConnectErrorLoggedRef.current) {
                console.warn(
                    'Realtime chat is temporarily unavailable. Live messages and notifications may not update until the connection recovers.',
                    error?.message || error
                );
                socketConnectErrorLoggedRef.current = true;
            }
            setAuthError('Realtime chat is temporarily unavailable.');
        });

        socketRef.current.on('new_chat_messages', (message) => {
            subscribersRef.current.forEach((callback) => callback(message));
        });

        socketRef.current.on('new_notifications', (notification) => {
            notificationsRef.current.forEach((callback) => callback(notification));
        });

        socketRef.current.on('chat_read_count', (newCount) => {
            countRef.current.forEach((callback) => callback(newCount));
        });

        socketRef.current.on('form_signed', (data) => {
            signedRef.current.forEach((callback) => callback(data));
        });

        socketRef.current.on('chat_error', (error) => {
            chatErrorRef.current.forEach((callback) => callback(error));
        });

        socketRef.current.connect();
    }, []);

    // ✅ Mount/unmount lifecycle
    useEffect(() => {
        initializeSocket();
        return () => {
            if (socketRef.current) {
                console.warn("🔴 Cleaning up socket...");
                socketRef.current.disconnect();
                socketRef.current = null;
                if (authTimeoutRef.current) {
                    clearTimeout(authTimeoutRef.current);
                }
            }
        };
    }, [initializeSocket]);

    // ✅ Unified auth method with timeout and retry logic
    const authenticateNonUserSocket = useCallback(async ({ memberId, memberType, groupId }) => {
        const socket = socketRef.current;

        if (!socket) {
            console.error('❌ Socket not initialized yet');
            setAuthError('Socket not initialized');
            return;
        }

        if (!socket.connected) {
            console.error('❌ Socket not connected');
            setAuthError('Socket not connected');
            return;
        }

        if (!memberId || !memberType || !groupId) {
            console.error('⚠️ Missing required auth params:', { memberId, memberType, groupId });
            setAuthError('Missing required parameters');
            return;
        }

        // ✅ NEW: Prevent duplicate auth attempts
        if (pendingAuthRef.current === `${memberType}-${memberId}`) {
            console.warn('⚠️ Auth already pending for this user');
            return;
        }

        try {
            console.log(`🔐 Requesting chat token for ${memberType} ${memberId}...`);
            pendingAuthRef.current = `${memberType}-${memberId}`;
            setIsAuthenticated(false);
            setAuthError(null);

            const chatToken = await requestNonUserChatToken({
                memberId,
                memberType,
                groupId,
            });

            console.log(`📤 Emitting authenticate event for ${memberType}...`);
            socket.emit('authenticate', {
                member_id: String(memberId),
                member_type: String(memberType).toUpperCase(),
                group_id: String(groupId),
                chat_token: chatToken,
            });

            // ✅ NEW: Set timeout to detect if auth hangs
            if (authTimeoutRef.current) {
                clearTimeout(authTimeoutRef.current);
            }
            authTimeoutRef.current = setTimeout(() => {
                console.warn(`⚠️ Auth timeout for ${memberType} ${memberId} (no response after 10s)`);
                setAuthError('Authentication timeout - server not responding');
                pendingAuthRef.current = null;
            }, 10000);

        } catch (error) {
            console.error('❌ Failed to get chat token:', error);
            setAuthError(error.message || 'Failed to authenticate');
            pendingAuthRef.current = null;
            setIsAuthenticated(false);
        }
    }, [requestNonUserChatToken]);

    const authSocket = useCallback((agentId = null, groupId = null, memberType = 'AGENT') => {
        authenticateNonUserSocket({ memberId: agentId, memberType, groupId });
    }, [authenticateNonUserSocket]);

    const authLeadSocket = useCallback((leadId, groupId) => {
        console.log(`👤 Authenticating LEAD ${leadId} in group ${groupId}`);
        authenticateNonUserSocket({
            memberId: leadId,
            memberType: 'LEAD',
            groupId,
        });
    }, [authenticateNonUserSocket]);

    const sendMessage = useCallback((payload, timeoutMs = 8000) => {
        const socket = socketRef.current;

        if (!socket || !socket.connected) {
            return Promise.resolve({
                ok: false,
                reason: 'socket_unavailable',
                message: 'Socket is not connected',
            });
        }

        if (!isAuthenticated) {
            return Promise.resolve({
                ok: false,
                reason: 'not_authenticated',
                message: 'Socket authentication pending',
            });
        }

        return new Promise((resolve) => {
            let timedOut = false;
            const timer = setTimeout(() => {
                timedOut = true;
                resolve({
                    ok: false,
                    reason: 'ack_timeout',
                    message: 'No acknowledgement from chat server',
                });
            }, timeoutMs);

            socket.emit('send_message', payload, (ack) => {
                if (timedOut) return;
                clearTimeout(timer);
                if (ack && typeof ack === 'object') {
                    resolve(ack);
                    return;
                }
                resolve({ ok: true, message: 'Message sent' });
            });
        });
    }, [isAuthenticated]);

    // ✅ Subscription methods (already fixed)
    const subscribeToNewNotifications = useCallback((callback) => {
        notificationsRef.current.push(callback);
        return () => {
            notificationsRef.current = notificationsRef.current.filter((cb) => cb !== callback);
        };
    }, []);

    const subscribeToNewMessages = useCallback((callback) => {
        subscribersRef.current.push(callback);
        return () => {
            subscribersRef.current = subscribersRef.current.filter((cb) => cb !== callback);
        };
    }, []);

    const subscribeToReadMessage = useCallback((callback) => {
        countRef.current.push(callback);
        return () => {
            countRef.current = countRef.current.filter((cb) => cb !== callback);
        };
    }, []);

    const subscribeToSignForm = useCallback((callback) => {
        signedRef.current.push(callback);
        return () => {
            signedRef.current = signedRef.current.filter((cb) => cb !== callback);
        };
    }, []);

    const subscribeToScreeningStatus = useCallback((callback) => {
        screeningRef.current.push(callback);
        return () => {
            screeningRef.current = screeningRef.current.filter((cb) => cb !== callback);
        };
    }, []);

    const subscribeToScreeningPaymentStatus = useCallback((callback) => {
        screeningPaymentRef.current.push(callback);
        return () => {
            screeningPaymentRef.current = screeningPaymentRef.current.filter((cb) => cb !== callback);
        };
    }, []);

    const subscribeToChatError = useCallback((callback) => {
        chatErrorRef.current.push(callback);
        return () => {
            chatErrorRef.current = chatErrorRef.current.filter((cb) => cb !== callback);
        };
    }, []);

    // ✅ FIXED: Use state, not ref, for memoization
    const value = React.useMemo(() => ({
        socket: socketRef.current,
        isSocketReady: socketReady,  // ✅ Use state, not ref
        isAuthenticated,
        authError,  // ✅ NEW: Expose error to UI
        authSocket,
        authLeadSocket,
        sendMessage,
        subscribeToNewMessages,
        subscribeToNewNotifications,
        subscribeToReadMessage,
        subscribeToSignForm,
        subscribeToScreeningStatus,
        subscribeToScreeningPaymentStatus,
        subscribeToChatError,
    }), [
        socketReady,
        isAuthenticated,
        authError,
        authSocket,
        authLeadSocket,
        sendMessage,
        subscribeToNewMessages,
        subscribeToNewNotifications,
        subscribeToReadMessage,
        subscribeToSignForm,
        subscribeToScreeningStatus,
        subscribeToScreeningPaymentStatus,
        subscribeToChatError,
    ]);

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};
